import type { OperationTypeDefinitionNode } from "graphql";

import {
    ApolloClient,
    ApolloLink,
    defaultDataIdFromObject,
    HttpLink,
    InMemoryCache,
    split,
    type NormalizedCacheObject,
    type ServerError,
    type ServerParseError
} from "@apollo/client/core";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { createApolloProvider } from "@vue/apollo-option";

import bus from "@/bus";
import config from "@/config";
import { getIdToken } from "@/services/Auth";
import { getObjectValue } from "@/utils";
import { useCommonStore } from "@/store/pinia/common";
import { API_ROUTES } from "@/constants";

import { LocalDevLink } from "./local-dev";

let loadingCount = 0;

type LocalContext = {
    useLocalDevLink?: boolean;
};

const errorLink = onError(({ operation, response, graphQLErrors, networkError }) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if ((operation.query.definitions[0] as OperationTypeDefinitionNode | undefined)?.operation === "query") {
        if (graphQLErrors && getObjectValue(graphQLErrors, "length")) {
            if (response) {
                response.errors = undefined;
            }

            bus.$emit("apolloErrorEvent", {
                graphQLErrors: [...graphQLErrors],
                message: `graphQLErrors: ${graphQLErrors[0]?.message}`
            });
        }
    }

    /*
       We do not want the user to be redirected away from the page they are on if
       we have an error for some operations, preferring to handle the error at
       the component level.
    */
    const operationsToIgnore = [
        "PublishVariationsXmlFeed",
        "UnpublishVariationsXmlFeed",
        "DownloadDeliverable",
        "GenerateCreativeInsightsReport"
    ];

    if (networkError && !operationsToIgnore.includes(operation.operationName)) {
        const statusCode = (networkError as ServerParseError | ServerError).statusCode;

        if (statusCode === 409) {
            bus.$emit("userNotFound");
            return;
        }

        // Not authenticated (401/403) — go to login, not the error page.
        if (statusCode === 401 || statusCode === 403) {
            bus.$emit("unauthorized");
            return;
        }

        if ((operation.getContext() as LocalContext).useLocalDevLink) {
            bus.$emit("localDevNetworkError");
        } else {
            bus.$emit("networkError");
        }
    }
});

const cache = new InMemoryCache({
    dataIdFromObject: object => {
        const typename = "__typename";
        switch (object[typename]) {
            case "MediaItem": {
                /*
                This is required otherwise an image mediaItem that is used twice in
                a campaign will only have the resizeSettings from the first instance
                of that mediaItem.
            */
                let cacheKey = `${object[typename]}:${object.id as string}`;
                if (object.overwriteId) {
                    cacheKey += `:${object.overwriteId as string}`;
                }
                return cacheKey;
            }
            case "Editable": {
                // Because defaultValues depend on campaign we need to take them into account when creating a cache key
                return `${object[typename]}:${object._id as string}:${JSON.stringify(object.defaultValues)}`;
            }
            default:
                return defaultDataIdFromObject(object);
        }
    }
});

export let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

export function initApollo() {
    const commonStore = useCommonStore();

    function graphQLURI() {
        return commonStore.baseApiURL + API_ROUTES.GRAPHQL;
    }

    const batchLink = new BatchHttpLink({ uri: graphQLURI });
    const httpLink = new HttpLink({ uri: graphQLURI });
    const baseLink = split(operation => operation.operationName === "UpdateResizedTemplate", httpLink, batchLink);

    const httpWithAuthLink = setContext(async () => {
        return {
            // Add ID Token as auth header for API calls
            headers: { Authorization: await getIdToken() }
        };
    }).concat(baseLink);

    const localDevLink = new LocalDevLink({ uri: config.getHoxtonCliUri() });

    const retryLink = new RetryLink({
        delay: count => count * 1000 * Math.random(),
        attempts: {
            max: 5
        }
    });

    const splitLink = retryLink.split(
        operation => {
            return !!(operation.getContext() as LocalContext).useLocalDevLink;
        },
        localDevLink,
        httpWithAuthLink
    );

    const apolloLink = ApolloLink.from([errorLink, splitLink]);

    apolloClient = new ApolloClient({
        cache,
        link: apolloLink,
        connectToDevTools: true
    });

    const apolloProvider = createApolloProvider({
        defaultClient: apolloClient,

        defaultOptions: {
            $loadingKey: "loading"
        },

        watchLoading(isLoading, countModifier) {
            loadingCount = loadingCount + countModifier;

            bus.$emit("apolloLoadEvent", loadingCount);

            return loadingCount;
        },

        errorHandler(error) {
            bus.$emit("apolloErrorEvent", error as Error);
        }
    });

    return apolloProvider;
}
